import { useCallback, useState } from 'react';
import type { DirectedCastRole } from 'fym-shared/src/music24/directedVideoApi';
import { usePostFileMutation } from '../../../redux/api/fymAPI';
import { directedActionError, type DirectedActionError } from '../directedErrors';
import { useAddDirectedCastMutation, type DirectedVideoArgs } from '../directedVideoApi';
import { canUploadCast, castUploadRefusal, type CastUploadRefusal } from './castRules';

/** `artistPicture`: the artist picture could not go into the cast (no picture any more, a bucket error). */
export type CastUploadError = CastUploadRefusal | 'upload' | 'artistPicture' | DirectedActionError;

export interface CastUploadItem {
  key: string;
  name: string;
}

let nextKey = 0;

/**
 * The photo uploads of the cast (design b1, b3 "Uploading"): each photo goes up as a file of the
 * account (`POST /accounts/:id/files`, the upload of the cover editor, `useReferenceUpload`), then
 * into the cast (`POST .../directed/:uuid/cast`). One photo at a time keeps the cast order. Every
 * photo needs the consent tick (review I3): with no tick nothing goes up and the error is `consent`.
 */
export function useCastUpload(args: DirectedVideoArgs) {
  const [postFile] = usePostFileMutation();
  const [addCast] = useAddDirectedCastMutation();
  const [uploads, setUploads] = useState<CastUploadItem[]>([]);
  const [error, setError] = useState<CastUploadError | null>(null);

  const uploadOne = useCallback(
    async (file: File, role: DirectedCastRole): Promise<void> => {
      const refusal = castUploadRefusal(file);
      if (refusal !== null) {
        setError(refusal);
        return;
      }
      nextKey += 1;
      const item = { key: `upload-${nextKey}`, name: file.name };
      setUploads((current) => [...current, item]);
      try {
        const formData = new FormData();
        formData.append('file', file, file.name);
        let fileId: number;
        try {
          fileId = (await postFile({ accountId: args.accountId, formData }).unwrap()).id;
        } catch (caught) {
          console.error('Directed video: the cast photo did not upload', caught);
          setError('upload');
          return;
        }
        try {
          await addCast({ ...args, fileId, role, label: '', consent: true }).unwrap();
        } catch (caught) {
          console.error('Directed video: the photo was not added to the cast', caught);
          setError(directedActionError(caught));
        }
      } finally {
        setUploads((current) => current.filter((one) => one.key !== item.key));
      }
    },
    [addCast, args, postFile]
  );

  const upload = useCallback(
    async (files: readonly File[], role: DirectedCastRole, consent: boolean, room: number): Promise<void> => {
      setError(null);
      if (!canUploadCast(consent)) {
        setError('consent');
        return;
      }
      for (const file of files.slice(0, Math.max(0, room))) {
        // One after the other: the cast keeps the order of the files.
        // eslint-disable-next-line no-await-in-loop
        await uploadOne(file, role);
      }
    },
    [uploadOne]
  );

  const clearError = useCallback(() => setError(null), []);

  return { upload, uploads, error, clearError };
}
